class RiseIngredients extends HTMLElement {
  #index = 0;
  #pointerId = null;
  #startX = 0;
  #deltaX = 0;
  #dragging = false;

  connectedCallback() {
    this.track = this.querySelector('[data-rise-ingredients-track]');
    this.viewport = this.querySelector('.rise-ingredients__viewport');
    this.slides = Array.from(this.querySelectorAll('[data-rise-ingredients-slide]'));
    this.prevBtn = this.querySelector('[data-rise-ingredients-prev]');
    this.nextBtn = this.querySelector('[data-rise-ingredients-next]');

    if (!this.track || this.slides.length === 0) return;

    this.prevBtn?.addEventListener('click', () => this.goTo(this.#index - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.#index + 1));
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.viewport?.addEventListener('pointerdown', this.#onPointerDown);
    this.viewport?.addEventListener('pointermove', this.#onPointerMove);
    this.viewport?.addEventListener('pointerup', this.#onPointerUp);
    this.viewport?.addEventListener('pointercancel', this.#onPointerUp);
    this.viewport?.addEventListener('pointerleave', this.#onPointerUp);

    this.#index = 0;
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onResize);
    this.viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    this.viewport?.removeEventListener('pointermove', this.#onPointerMove);
    this.viewport?.removeEventListener('pointerup', this.#onPointerUp);
    this.viewport?.removeEventListener('pointercancel', this.#onPointerUp);
    this.viewport?.removeEventListener('pointerleave', this.#onPointerUp);
  }

  #onResize = () => {
    window.clearTimeout(this._resizeTimer);
    this._resizeTimer = window.setTimeout(() => {
      this.#index = Math.min(this.#index, this.maxIndex);
      this.update();
    }, 120);
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#deltaX = 0;
    this.#dragging = true;
    this.track.style.transition = 'none';
    this.viewport?.setPointerCapture?.(event.pointerId);
  };

  #onPointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId) return;
    this.#deltaX = event.clientX - this.#startX;
    const base = this.#index * (this.slideStep);
    this.track.style.transform = `translate3d(${-base + this.#deltaX}px, 0, 0)`;
  };

  #onPointerUp = (event) => {
    if (!this.#dragging || (event.pointerId != null && event.pointerId !== this.#pointerId)) return;
    this.#dragging = false;
    this.#pointerId = null;

    const threshold = Math.min(80, this.slideStep * 0.2);
    if (this.#deltaX <= -threshold) this.goTo(this.#index + 1);
    else if (this.#deltaX >= threshold) this.goTo(this.#index - 1);
    else this.update(true);

    this.#deltaX = 0;
  };

  get gap() {
    return window.innerWidth >= 1024 ? Number(this.dataset.gap || 24) : 20;
  }

  get perView() {
    const width = window.innerWidth;
    if (width >= 1024) return 3;
    if (width >= 640) return 2;
    return 1;
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - this.perView);
  }

  get slideStep() {
    if (!this.slides[0]) return 0;
    return this.slides[0].getBoundingClientRect().width + this.gap;
  }

  goTo(index) {
    this.#index = Math.max(0, Math.min(index, this.maxIndex));
    this.update(true);
  }

  update(animate = false) {
    if (!this.track || !this.slides[0]) return;

    const offset = this.#index * this.slideStep;
    this.track.style.transition = animate ? 'transform 500ms ease' : 'none';
    this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;

    const atStart = this.#index <= 0;
    const atEnd = this.#index >= this.maxIndex;

    this.prevBtn?.classList.toggle('is-disabled', atStart);
    this.nextBtn?.classList.toggle('is-disabled', atEnd);
    this.prevBtn?.toggleAttribute('disabled', atStart);
    this.nextBtn?.toggleAttribute('disabled', atEnd);
  }
}

if (!customElements.get('rise-ingredients-component')) {
  customElements.define('rise-ingredients-component', RiseIngredients);
}
